AoPS.Utils.initKeyChain(AoPS, "Ebooks", "Models", "Book");

AoPS.Ebooks.Models.Book = (function () {
	// Dependencies
	var LoadUtils = AoPS.LoadUtils;
	var EbkMBase = AoPS.Ebooks.Models.Base;
	var EbkModels = AoPS.Ebooks.Models; // .Problem, .Hint, .Chapter, .Section, .EdgeSection
	var EbkUrlMain = AoPS.Ebooks.Url.main;

	return EbkMBase.BaseModel.extend({
		id_attribute: "book_id",
		defaults: {
			has_access: false,
			can_cancel: false,
			code: "",
			cmty_category_id: 0,
			store_sku: "",
			title: "",
			short_title: "",
			authors: [],
			last_viewed_section_id: 0,
			start_access_at: "",
			section_code_table: null, // code : id pairs
		},
		/** Additional attributes:
			master: Master model owning this one; guaranteed to be set
			chapters: collection of Chapter models
			sections: collection of Section models
			problems: collection of Problem models
			free_section: section model denoting the section available for free

			label_fragments: Object with label ID key to fragment ID value
			paragraph_fragments: Object with paragraph ID key to fragment ID value
			problem_fragments: Object with problem ID key to fragment ID value
		*/

		initialize: function () {
			this.set("label_fragments", {});
			this.set("paragraph_fragments", {});
			this.set("problem_fragments", {});

			this.setLoader(
				new LoadUtils.LoadManager({
					loadHandler: this.loadBook,
					context: this,
				})
			);

			this.section_loader = new LoadUtils.SingleLoadQueue({
				loadHandler: this.loadSectionContent,
				context: this,
			});

			this.set("free_section", null);

			// Convenience
			var book = this;
			function createColl(model_attr, ModelType, comparator) {
				var coll = new EbkMBase.BaseCollection();
				book.set(model_attr, coll);
				coll.setCreateFunction(function (id) {
					return new ModelType({id: id, book: book});
				});
				if (comparator) {
					coll.comparator = comparator;
				}
			}

			createColl("problems", EbkModels.Problem);
			createColl("hints", EbkModels.Hint);
			createColl("chapters", EbkModels.Chapter, "order");
			createColl("sections", EbkModels.Section, "order");
			createColl("edge_sections", EbkModels.EdgeSection);
		},

		loadBook: function (callback) {
			var master = this.get("master");
			master.requestLoad(function (ok, data) {
				if (!ok) {
					master.get("books").remove(this.id);
					return callback(ok, data);
				}

				if (!this.get("title")) {
					master.get("books").remove(this.id);
					callback(false, {
						error_code: "E_NOT_FOUND",
						error_msg:
							"The requested book was not found, or you do not have access to it.",
					});
				} else {
					callback(true, {});
				}
			}, this);
		},

		unpack: function (data) {
			if (!data) {
				return;
			}

			this.setFields(data);

			_.extend(this.get("label_fragments"), data.label_fragments || {});
			_.extend(this.get("paragraph_fragments"), data.paragraph_fragments || {});
			_.extend(this.get("problem_fragments"), data.problem_fragments || {});

			this.get("chapters").unpackAll(data.chapters, "fragment_id");
			this.get("chapters").sort();

			this.get("sections").unpackAll(data.sections, "fragment_id");
			this.get("sections").sort();
			_.invoke(this.get("chapters").models, "sortSections");

			this.get("edge_sections").unpackAll(data.edge_sections, "fragment_code");

			this.get("problems").unpackAll(data.problems, "problem_id");

			this.get("hints").unpackAll(data.hints, "hint_id");

			this.markAsLoaded(true);
		},

		requestSectionContent: function (section_id, callback, context) {
			this.requestLoad(function (ok) {
				if (!ok) {
					return;
				}
				this.section_loader.requestLoad(section_id, callback, context);
			}, this);
		},

		markSectionAsLoaded: function (section_id, data) {
			this.section_loader.markAsLoaded(section_id, true, data || {});
		},

		loadSectionContent: function (section_id, callback) {
			var section = this.get("sections").get(section_id);
			if (section) {
				section.loadContent(false, callback);
			} else {
				callback(false, {
					error_code: "E_NOT_FOUND",
					error_msg: "The requested section does not exist.",
				});
			}
		},

		loadFreePreview: function (callback, context) {
			function onLoad(ok, data) {
				if (!ok) {
					return callback(ok, data);
				}
				this.unpack(data.response.book);

				var free_fragment_id = data.response.free_section.fragment_id;
				var free_fragment = this.get("sections").getOrCreate(free_fragment_id);
				this.set("free_section", free_fragment);
				free_fragment.unpack(data.response.free_section);
				callback(ok, free_fragment);
			}

			callback = LoadUtils.makeCallback(callback, context);

			if (this.get("free_section")) {
				setTimeout(_.partial(callback, true, this.get("free_section")), 0);
			} else {
				this.requestLoad(function (ok, data) {
					if (!ok) {
						return callback(ok, data);
					}
					EbkMBase.ajax.run(
						"load_free_preview",
						{
							user_id: this.get("master").get("user_id"),
							book_id: this.id,
						},
						_.bind(onLoad, this)
					);
				}, this);
			}
		},

		cancelPurchase: function (reason, callback, context) {
			callback = LoadUtils.makeCallback(callback, context);

			EbkMBase.ajax.run(
				"cancel_purchase",
				{
					user_id: this.get("master").get("user_id"),
					book_id: this.id,
					reason: reason || "",
				},
				callback
			);
		},

		// Callback accepts ok, data. If ok is true, data is the fragment ID.
		findLabel: function (label_id, callback, context) {
			var fragments = this.get("label_fragments");

			function onFind(ok, data) {
				if (!ok) {
					return callback(ok, data);
				}
				this.unpack(data.response.book);
				callback(ok, data.response.fragment_id);
			}

			callback = LoadUtils.makeCallback(callback, context);

			var fragment_id = fragments[label_id];
			if (fragment_id) {
				setTimeout(_.partial(callback, true, fragment_id), 0);
			} else {
				this.requestLoad(function (ok, data) {
					if (!ok) {
						return callback(ok, data);
					}
					EbkMBase.ajax.run(
						"find_label",
						{
							user_id: this.get("master").get("user_id"),
							book_id: this.id,
							label_id: label_id,
						},
						_.bind(onFind, this)
					);
				}, this);
			}
		},

		// Callback accepts ok, data. If ok is true, data is the fragment ID.
		findParagraph: function (paragraph_id, callback, context) {
			var fragments = this.get("paragraph_fragments");

			function onFind(ok, data) {
				if (!ok) {
					return callback(ok, data);
				}
				this.unpack(data.response.book);
				callback(ok, data.response.fragment_id);
			}

			callback = LoadUtils.makeCallback(callback, context);

			var fragment_id = fragments[paragraph_id];
			if (fragment_id) {
				setTimeout(_.partial(callback, true, fragment_id), 0);
			} else {
				this.requestLoad(function (ok, data) {
					if (!ok) {
						return callback(ok, data);
					}
					EbkMBase.ajax.run(
						"find_paragraph",
						{
							user_id: this.get("master").get("user_id"),
							book_id: this.id,
							paragraph_id: paragraph_id,
						},
						_.bind(onFind, this)
					);
				}, this);
			}
		},

		// Callback accepts ok, data. If ok is true, data is the fragment ID.
		findProblem: function (problem_id, callback, context) {
			var fragments = this.get("problem_fragments");

			function onFind(ok, data) {
				if (!ok) {
					return callback(ok, data);
				}
				this.unpack(data.response.book);
				callback(ok, data.response.fragment_id);
			}

			callback = LoadUtils.makeCallback(callback, context);

			var fragment_id = fragments[problem_id];
			if (fragment_id) {
				setTimeout(_.partial(callback, true, fragment_id), 0);
			} else {
				this.requestLoad(function (ok, data) {
					if (!ok) {
						return callback(ok, data);
					}
					EbkMBase.ajax.run(
						"find_problem",
						{
							user_id: this.get("master").get("user_id"),
							book_id: this.id,
							problem_id: problem_id,
						},
						_.bind(onFind, this)
					);
				}, this);
			}
		},

		findSearchMatches: function (search, callback, context) {
			function onSearch(ok, data) {
				if (!ok) {
					return callback(ok, data);
				}
				callback(true, data.response.search_matches);
			}

			callback = LoadUtils.makeCallback(callback, context);

			EbkMBase.ajax.run(
				"search_matches",
				{
					user_id: this.get("master").get("user_id"),
					book_id: this.id,
					search: search,
				},
				_.bind(onSearch, this)
			);
		},

		findSearchResults: function (search, callback, context) {
			function onSearch(ok, data) {
				if (!ok) {
					return callback(ok, data);
				}

				data = _.map(
					data.response.search_results,
					function (loc_data) {
						var section_id = this.getAsSectionId(loc_data.fragment_id);
						if (!section_id) {
							return null;
						}
						return {
							search: search,
							section_id: section_id,
							paragraph_id: loc_data.paragraph_id,
							order: this.get("sections").get(section_id).get("order"),
						};
					},
					this
				);
				data = _.sortBy(_.compact(data), "order");
				callback(true, data);
			}

			callback = LoadUtils.makeCallback(callback, context);

			EbkMBase.ajax.run(
				"search_results",
				{
					user_id: this.get("master").get("user_id"),
					book_id: this.id,
					search: search,
				},
				_.bind(onSearch, this)
			);
		},

		getFragmentIdFromCode: function (code) {
			var table = this.get("section_code_table");
			var fragment_id = table && table[code];
			return fragment_id || 0;
		},

		getFragmentCodeFromId: function (id) {
			id = this.getAsSectionId(id);
			var model = this.get("sections").get(id);
			return model && model.get("fragment_code");
		},

		getTags: function () {
			var is_embedded = this.get("master").get("is_embedded");
			return is_embedded ? ["Book"] : [];
		},

		// Pass a fragment ID in. If it's a chapter ID, returns the ID of the first
		// section. Otherwise, leave unchanged.
		getAsSectionId: function (fragment_id) {
			var chapter = this.get("chapters").get(fragment_id);
			return chapter ? chapter.getFirstSectionId() : fragment_id;
		},

		getFirstSectionId: function () {
			return this.get("sections").at(0) && this.get("sections").at(0).id;
		},

		getByLine: function () {
			if (!this.get("authors")) {
				return "";
			}
			return "by " + this.get("authors").join(", ");
		},

		getCoverUrl: function () {
			return EbkUrlMain.getImage(this.get("code") + "-cover.png");
		},

		resetAllProblems: function (callback, context) {
			callback = LoadUtils.makeCallback(callback, context);

			EbkMBase.ajax.run(
				"reset_all_problems",
				{
					user_id: this.get("master").get("user_id"),
					book_id: this.id,
				},
				callback
			);
		},
	});
})();
